import AIAPIService from './AIAPIService.js'

/**
 * ContentGeneratorService - Converts API data and user prompts into visual content for digital signage
 * This service generates visual representations that can be displayed as images on screens
 */
class ContentGeneratorService {
  constructor() {
    this.apiService = new AIAPIService()
  }



  /**
   * Generate custom content from user prompt
   * @param {string} prompt - User's custom prompt
   * @param {string} style - Visual style (modern, classic, minimal)
   * @param {object} options - Additional options
   * @returns {Promise<{imageUrl: string, data: object}>}
   */
  async generateCustomContent(prompt, style = 'modern', options = {}) {
    try {
      if (!prompt || prompt.trim().length === 0) {
        throw new Error('Prompt is required for custom content generation')
      }

      // Create enhanced prompt for digital signage
      const enhancedPrompt = this.createCustomVisualDescription(prompt, style)
      
      // Generate image using AI services
      const imageResult = await this.apiService.generateImage(enhancedPrompt, {
        width: options.width || 1920,
        height: options.height || 1080,
        count: options.count || 1
      })
      
      if (!imageResult.success) {
        throw new Error(imageResult.error || 'Failed to generate image')
      }
      
      return {
        imageUrl: imageResult.images[0],
        data: { prompt, style, options },
        type: 'custom',
        prompt,
        style,
        source: imageResult.source
      }
    } catch (error) {
      console.error('Error generating custom content:', error)
      throw error
    }
  }



  /**
   * Create visual description for custom user prompt
   */
  createCustomVisualDescription(prompt, style) {
    const stylePrompts = {
      modern: 'modern digital display, clean typography, gradient background',
      classic: 'traditional display, serif fonts, elegant layout',
      minimal: 'minimalist design, simple icons, lots of white space'
    }

    // Enhance the user's prompt for digital signage
    return `A ${stylePrompts[style] || stylePrompts.modern} digital signage display showing: ${prompt}. Professional business layout with clear typography, high contrast, landscape format, suitable for digital displays`
  }

  /**
   * Generate content based on type and parameters
   * @param {string} contentType - Type of content to generate
   * @param {object} params - Parameters for content generation
   * @returns {Promise<{imageUrl: string, data: object}>}
   */
  async generateContent(contentType, params = {}) {
    switch (contentType.toLowerCase()) {
      case 'custom':
        return await this.generateCustomContent(params.prompt, params.style, params.options)
      
      default:
        throw new Error(`Unsupported content type: ${contentType}`)
    }
  }

  /**
   * Get available content types and their parameters
   */
  getAvailableContentTypes() {
    return {
      custom: {
        description: 'Generate custom content from your prompt',
        parameters: {
          prompt: { type: 'string', default: '', description: 'Describe what you want to display (e.g., "Welcome message", "Promotional banner", "Event announcement")' },
          style: { type: 'string', default: 'modern', options: ['modern', 'classic', 'minimal'], description: 'Visual style' }
        }
      }
    }
  }
}

export default ContentGeneratorService 